import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import * as microsoftTeams from "@microsoft/teams-js";
import { AxiosError } from "axios";
import { Flex, Checkbox, Button, Tooltip, SplitButton } from "@fluentui/react-northstar";
import { CheckmarkCircleIcon, EditIcon, NotesIcon, OpenOutsideIcon, LinkIcon, TrashCanIcon, PollIcon } from "@fluentui/react-icons-northstar";
import find from "lodash/find";
import FieldWithLabel from "../components/common/fieldWithLabel";
import Alert from "../components/common/alert";
import IReport from "../interfaces/IReport";

import { useAuth } from "../contexts/authContext";
import useClearAuthIssue from "../common/useClearAuthIssue";
import { useApp } from "../contexts/appContext";
import { deleteReport, updateReport } from "../services/dataService";
import { useInvalidateData, useMe, useReport } from "../queries";
import { parseDateToString } from "../common/dateOperations";
import { copyToClipboard, projectTimeToString, getVacationReportLink } from "../common/textOperations";
import { numberToString } from "../common/numberOperations";
import styles from "../common.module.scss";
import { temporaryProject, dovolenaProject } from "../constants";
import { getSettings } from "../services/settingsService";

const ReportDetail: React.FunctionComponent = () => {
    document.title = "Detail";
    const settings = getSettings();

    const { id } = useParams<{ id: string }>();
    const [errorMessage, setErrorMessage] = useState("");
    const [authIssue, setAuthIssue] = useState(true);
    const { state: authState } = useAuth();
    const { accessToken: token } = authState;
    useClearAuthIssue(authIssue, () => { setErrorMessage(""); setAuthIssue(false); });
    const history = useHistory();
    const { state: appState, dispatch } = useApp();

    const { report: loadedReport } = useReport(id);
    const { data: me } = useMe();
    const { invalidateReports } = useInvalidateData();

    // Completing the report updates it locally, the list is refreshed on the background
    const [completedReport, setCompletedReport] = useState<IReport>();
    const report = completedReport ?? loadedReport;

    const vacationReportLink = me ? getVacationReportLink(me.dataverseUserId) : undefined;
    const isTempProject: boolean = report?.project.subject === temporaryProject;
    const isVacation: boolean = report?.project.subject === dovolenaProject;

    const toggleLoading = (loading: boolean) => {
        dispatch({ type: "SET_LOADER", show: loading });
    };

    const asyncCall = async (callback: any) => {
        setErrorMessage("");
        setAuthIssue(false);

        toggleLoading(true);
        try {
            await callback;
            toggleLoading(false);
            invalidateReports();
        } catch (error) {
            const err: AxiosError = error as AxiosError;
            toggleLoading(false);
            if (err.response?.status === 401) {
                setErrorMessage("Přístupový token vypršel.");
                setAuthIssue(true);
            } else {
                setErrorMessage(err.message);
                console.log(err.response?.data);
            }
        }
    }

    const completeReport = async () => {
        if (!token || !report) { return; }
        await asyncCall(updateReport(token, { ...report, state: 1 }));
        setCompletedReport({ ...report, state: 1 });
    };

    const openOrCopyLink = (link: string) => {
        if (settings.copyLinks) {
            copyToClipboard(link);
        } else {
            window.open(link);
        }
    };

    const modalResultClose = async (data: any) => {
        if (!data.result) {
            return;
        }

        if (!token) { return; }
        await asyncCall(deleteReport(token, id));
        history.push("/");
    };

    const showModalDelete = () => {
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Potvrzení",
            size: {
                height: 160,
                width: 400
            },
            url: import.meta.env.VITE_BASE_URL + `/modal/report/confirmdelete`
        };

        microsoftTeams.dialog.url.open(dialogInfo, modalResultClose);
    };

    if (!report) {
        return null;
    }

    if (errorMessage) {
        return <Alert type="error" authIssue={authIssue}>{errorMessage}</Alert>;
    }

    const renderProjectTime = () => {
        if (report.project.plannedHours + report.project.remainingHours > 0) {
            return (
                <small>{projectTimeToString(report.project.plannedHours, report.project.remainingHours)}</small>
            );
        }

        return null;
    };

    return (
        <div>
            <Flex gap="gap.small" column className={styles.formBody}>
                <FieldWithLabel value={report.subject} label="Popis výkazu" />
                {report.description &&
                    <FieldWithLabel value={report.description} label="Interní poznámka" />
                }
                <Flex gap="gap.medium">
                    <FieldWithLabel value={parseDateToString(report.start)} label="Začátek" />
                    <FieldWithLabel value={parseDateToString(report.end)} label="Konec" />
                    <FieldWithLabel value={`${numberToString(report.durationInMinutes / 60)} h`} label="Doba&nbsp;trvání" />
                    {report.aiSavedHours !== undefined && report.aiSavedHours > 0 &&
                        <FieldWithLabel value={`${numberToString(report.aiSavedHours)} h`} label="AI-úspora&nbsp;času" />
                    }
                </Flex>
                <div>
                    <FieldWithLabel value={report.project.subject} label="Případ" />
                    {renderProjectTime()}
                </div>

                <Checkbox label="Dokončeno" checked={report.state === 1} disabled />
                <Checkbox label="Režie" checked={report.overhead} disabled />
                <Checkbox label="Mimo pracovní dobu na přání zákazníka" checked={report.requestedByCustomer} disabled />
                <Checkbox label="Extended hours" checked={report.extendedHours} disabled />
                <Checkbox label="Přesčas" checked={report.overtime} disabled />
                <Checkbox label="Shadowing" checked={report.shadowing} disabled />

                {report.kilometers !== undefined && report.kilometers > 0 && <FieldWithLabel value={report.kilometers?.toString()} label="Kilometry" />}
                {report.location && <FieldWithLabel value={report.location} label="Místo" />}
            </Flex>

            <div className={styles.footer} style={{ background: appState.backgroundColor }}>
                <Flex gap="gap.small" className={styles.centerContainer}>
                    {settings.copyLinks ?
                        <Tooltip
                            trigger={<Button text iconOnly icon={<LinkIcon />} onClick={() => openOrCopyLink(report.link)} />}
                            content="Kopírovat odkaz do CRM"
                        />
                        :
                        <Tooltip
                            trigger={<Button text iconOnly icon={<OpenOutsideIcon />} onClick={() => openOrCopyLink(report.link)} />}
                            content="Otevřít výkaz v CRM"
                        />
                    }

                    {isVacation && vacationReportLink &&
                        <Tooltip
                            trigger={<Button text iconOnly loading={appState.showLoader} disabled={appState.showLoader} icon={<PollIcon outline />} onClick={() => openOrCopyLink(vacationReportLink)} />}
                            content="Otevřít report dovolené"
                        />
                    }

                    <Tooltip
                        trigger={<Button text iconOnly icon={<NotesIcon outline />} onClick={() => history.push(`/report/new/${id}`)} />}
                        content="Kopírovat"
                    />

                    {report.state !== 1 ?
                        <Tooltip
                            trigger={<Button text iconOnly loading={appState.showLoader} disabled={appState.showLoader || isTempProject} icon={<CheckmarkCircleIcon outline />} onClick={completeReport} />}
                            content="Označit výkaz jako dokončený"
                        />
                        :
                        <div />
                    }
                    <Flex.Item push>
                        {!report.invoiceNumber && !report.project.isClosed && !report.project.parentIsClosed ?
                            <SplitButton
                                menu={[
                                    {
                                        key: "edit",
                                        content: "Upravit",
                                        icon: <EditIcon outline />,
                                        onClick: () => history.push(`/report/edit/${id}`)
                                    },
                                    report.state !== 1 &&
                                    {
                                        key: "delete",
                                        content: "Smazat",
                                        icon: <TrashCanIcon outline />,
                                        onClick: showModalDelete
                                    }
                                ]}
                                button={{
                                    content: "Upravit",
                                    icon: <EditIcon outline />,
                                    loading: appState.showLoader,
                                    onClick: () => history.push(`/report/edit/${id}`)
                                }}
                                primary
                                disabled={appState.showLoader}
                            />
                            :
                            <div />
                        }
                    </Flex.Item>
                </Flex>
            </div>
        </div>
    );
};

export default ReportDetail;
